import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import qs from 'query-string';
import ym from 'react-yandex-metrika';
import { useIntl } from 'react-intl';

import {
    teacherReserveModalModel,
    type TeacherReserveActivity,
    type TeacherReserveParticipant,
} from '@edu-frontend/modules/src/features/teacherReserve';
import {
    useGeneratePath,
    useStaticUrls,
    showIntlMessageNotification,
    NOTIFICATION_THEMES,
    GRAVITIES,
} from 'platform-components';
import { getMe } from 'platform-components/src/components/user/selectors';

import { COMMON_NOTIFICATION_DELAY } from 'common.components/constants';
import { loadLessonViewData, toggleModal as toggleLessonViewModal } from 'common.components/lesson-view.store/actions';

/** Отдает обработчик клика для карточки активности */
export const useActivityCardButtonHandler = ({
    referrerLinkId,
}: {
    referrerLinkId?: TeacherReserveParticipant['referrerLinkId'];
} = {}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const intl = useIntl();

    const me = useSelector(getMe);

    const { labLibrarySubject, deepLabLibrary, labTeacherTrainingCourses, labSubjectJournal, labLibrarySubjectTheme } =
        useStaticUrls();
    const generatePath = useGeneratePath();

    let path: string;
    let search: string;

    const handleInviteReferralClick = () => {
        path = window.location.origin + generatePath(deepLabLibrary, { subject: 'informatics' });

        search = '?' + qs.stringify({ ...qs.parse(history.location.search), grade: 9, referrerLinkId });

        const url = path + search;

        teacherReserveModalModel.setModalInfo({
            title: intl.formatMessage({ id: 'teacherReserve.referralModal.title' }),
            text: intl.formatMessage({ id: 'teacherReserve.referralModal.text' }),
            buttonText: intl.formatMessage({ id: 'teacherReserve.referralModal.buttonText' }),
            onButtonClick: () => {
                navigator.clipboard.writeText(url).then(() => {
                    dispatch(
                        showIntlMessageNotification({
                            closeDelay: COMMON_NOTIFICATION_DELAY,
                            theme: NOTIFICATION_THEMES.TOAST,
                            gravity: GRAVITIES.BOTTOM,
                            messageId: 'toast.refLinkCopied',
                        })
                    );
                });
            },
        });

        teacherReserveModalModel.toggleModal(true);
    };

    // eslint-disable-next-line complexity
    const handleActivityCardButtonClick = ({ id, viewSettings }: TeacherReserveActivity) => {
        ym('reachGoal', 'teacher_talent_pool_click', { user_id: me?.id, type: 'activity', activity_id: id });

        switch (viewSettings.activityType) {
            case 'lesson':
                dispatch(loadLessonViewData({ lessonId: Number(viewSettings.lessonId) }));
                dispatch(toggleLessonViewModal(true));
                break;

            case 'theme':
                history.push(
                    generatePath(labLibrarySubjectTheme, {
                        subject: 'informatics',
                        themeId: Number(viewSettings.themeId),
                    }) + history.location.search
                );
                break;

            case 'link':
                window.open(viewSettings.buttonLink || 'http://ya.ru');
                break;

            case 'referrer':
                handleInviteReferralClick();
                break;

            case 'kpk':
                path = generatePath(labTeacherTrainingCourses);

                search = history.location.search;
                search += (search.includes('?') ? '&' : '?') + `kpk_template_id=${viewSettings.kpkTemplateId}`;

                history.push(generatePath(labTeacherTrainingCourses) + search);
                break;

            case 'journal':
                // журнал именно для диагностики, поэтому ведем не на информатику, а на информатику + Я = 🤍
                history.push(
                    generatePath(labSubjectJournal, { subject: 'diagnostics_info_middle' }) + history.location.search
                );
                break;

            case 'rubricator':
            default:
                history.push(generatePath(labLibrarySubject, { subject: 'informatics' }) + history.location.search);
        }
    };
    return { handleActivityCardButtonClick, handleInviteReferralClick };
};
